import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../lib/axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Download, Printer, Ship, CheckCircle2, XCircle, Settings2, Package, Wrench, Ruler } from 'lucide-react';
import { toast } from 'sonner';
import { processImageUrl } from '../utils/imageConverter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}` }
});

const TIPO_ORDER = ['servico', 'especificacao', 'produto'];
const TIPO_LABELS = { servico: 'SERVIÇOS', especificacao: 'ESPECIFICAÇÕES', produto: 'PRODUTOS' };
const TIPO_LABELS_SINGULAR = { servico: 'Serviço', especificacao: 'Especificação', produto: 'Produto' };
const TIPO_COLORS = {
  servico: { bg: '#059669', text: '#fff' },
  especificacao: { bg: '#2563eb', text: '#fff' },
  produto: { bg: '#d97706', text: '#fff' },
};

const EspecificacaoPDF = () => {
  const { projectId, embarcacaoIndex } = useParams();
  const navigate = useNavigate();
  const printRef = useRef();
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [embarcacao, setEmbarcacao] = useState(null);
  const [vesselModel, setVesselModel] = useState(null);
  const [fornecedoresMap, setFornecedoresMap] = useState({});

  // Filtros de impressao
  const [showFilterDialog, setShowFilterDialog] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    exibirFornecedores: true,
    exibirValores: true,
    exibirProdutosVinculados: true,
    incluirTermoAceite: true,
    itensExcluidos: {},
  });
  // Bloqueio de visualizacao de custos por permissao (role=producao ou pode_ver_custos=false)
  const [podeVerCustos, setPodeVerCustos] = useState(true);

  useEffect(() => {
    document.title = "Caderno de Especificações — NautilusOS";
    fetchData();
  }, [projectId, embarcacaoIndex]);

  const fetchData = async () => {
    try {
      const [projectRes, fornecedoresRes, meRes] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get('/fornecedores'),
        api.get('/auth/me')
      ]);

      // Permissao de custos: producao ou flag pode_ver_custos=false NAO veem valores
      const userData = meRes.data || {};
      const visivel = userData.pode_ver_custos !== false && userData.role !== 'producao';
      setPodeVerCustos(visivel);
      if (!visivel) {
        // Forcar valores ocultos no preview/PDF
        setFilterOptions(prev => ({ ...prev, exibirValores: false }));
      }

      const projectData = projectRes.data;
      setProject(projectData);

      const fMap = {};
      (fornecedoresRes.data || []).forEach(f => { fMap[f.id] = f; });
      setFornecedoresMap(fMap);

      const embIndex = parseInt(embarcacaoIndex);
      if (projectData.embarcacoes && projectData.embarcacoes[embIndex]) {
        const emb = projectData.embarcacoes[embIndex];
        setEmbarcacao(emb);
        if (emb.vessel_model_id) {
          const modelRes = await api.get(`/vessel-models/${emb.vessel_model_id}`);
          setVesselModel(modelRes.data);
        }
      }
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Organizar checklist: Servicos -> Especificacoes -> Produtos, agrupados por categoria
  const organizedChecklist = useMemo(() => {
    if (!embarcacao) return [];
    const checklist = embarcacao.checklist_preenchido || [];

    return TIPO_ORDER.map(tipo => {
      const itens = checklist.filter(i => {
        if (tipo === 'produto') return i.tipo_item === 'produto' || !i.tipo_item;
        return i.tipo_item === tipo;
      }).filter(i => !filterOptions.itensExcluidos[i.id || `${i.descricao}_${i.numero}`]);

      if (itens.length === 0) return null;

      const categorias = [...new Set(itens.map(i => i.categoria || 'Geral'))].sort();
      const grupos = categorias.map(cat => ({
        categoria: cat,
        itens: itens.filter(i => (i.categoria || 'Geral') === cat),
      }));

      return { tipo, label: TIPO_LABELS[tipo], grupos, total: itens.length };
    }).filter(Boolean);
  }, [embarcacao, filterOptions.itensExcluidos]);

  // Calcular valor total
  const valorTotal = useMemo(() => {
    if (!embarcacao) return 0;
    return (embarcacao.checklist_preenchido || [])
      .filter(i => !filterOptions.itensExcluidos[i.id || `${i.descricao}_${i.numero}`])
      .reduce((sum, item) => {
        const custos = (item.itens_custo || []).reduce((s, c) => s + (parseFloat(c.valor_total) || 0), 0);
        return sum + custos;
      }, 0);
  }, [embarcacao, filterOptions.itensExcluidos]);

  const handlePrint = () => window.print();
  const handleDownload = () => {
    toast.info('Use Ctrl+P ou Cmd+P e selecione "Salvar como PDF"');
    setTimeout(() => window.print(), 500);
  };

  // Toggle exclusao de item individual (logica invertida: marcar = excluir)
  const toggleItemExcluido = (key) => {
    setFilterOptions(prev => ({
      ...prev,
      itensExcluidos: { ...prev.itensExcluidos, [key]: !prev.itensExcluidos[key] }
    }));
  };

  // Restaurar todos (desmarcar todos = incluir todos)
  const incluirTodos = () => {
    setFilterOptions(prev => ({ ...prev, itensExcluidos: {} }));
  };

  // Excluir todos
  const excluirTodos = () => {
    const checklist = embarcacao?.checklist_preenchido || [];
    const excluidos = {};
    checklist.forEach(i => { excluidos[i.id || `${i.descricao}_${i.numero}`] = true; });
    setFilterOptions(prev => ({ ...prev, itensExcluidos: excluidos }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!project || !embarcacao) {
    return (
      <div className="p-6">
        <Card><CardContent className="py-12 text-center">
          <Ship className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Dados não encontrados</h3>
          <Button onClick={() => navigate(-1)} variant="outline"><ArrowLeft className="mr-2 h-4 w-4" />Voltar</Button>
        </CardContent></Card>
      </div>
    );
  }

  const checklist = embarcacao.checklist_preenchido || [];
  const itensComFoto = checklist.filter(i => i.foto_url && !filterOptions.itensExcluidos[i.id || `${i.descricao}_${i.numero}`]);
  const fornecedoresUsados = [...new Set(
    checklist.filter(c => c.fornecedor_id && !filterOptions.itensExcluidos[c.id || `${c.descricao}_${c.numero}`])
      .map(c => c.fornecedor_id)
  )];
  const totalItensVisiveis = checklist.filter(i => !filterOptions.itensExcluidos[i.id || `${i.descricao}_${i.numero}`]).length;
  const totalExcluidos = Object.values(filterOptions.itensExcluidos).filter(Boolean).length;

  // Agrupar itens do dialog por tipo para melhor organizacao
  const checklistByTipo = TIPO_ORDER.map(tipo => ({
    tipo,
    label: TIPO_LABELS[tipo],
    itens: checklist.filter(i => {
      if (tipo === 'produto') return i.tipo_item === 'produto' || !i.tipo_item;
      return i.tipo_item === tipo;
    })
  })).filter(g => g.itens.length > 0);

  return (
    <>
      {/* Toolbar - nao imprime */}
      <div className="print:hidden sticky top-0 z-10 bg-white border-b p-4 flex items-center justify-between" data-testid="pdf-toolbar">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)} data-testid="pdf-back-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-lg font-bold">Preview do Caderno de Especificações</h1>
            <p className="text-sm text-gray-500">{project.name} — Embarcação #{(parseInt(embarcacaoIndex) + 1).toString().padStart(2, '0')}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowFilterDialog(true)} data-testid="pdf-filter-btn">
            <Settings2 className="mr-2 h-4 w-4" />
            Opções de Impressão
            {totalExcluidos > 0 && (
              <Badge className="ml-2 bg-red-100 text-red-700 text-xs">{totalExcluidos} oculto(s)</Badge>
            )}
          </Button>
          <Button variant="outline" onClick={handlePrint} data-testid="pdf-print-btn">
            <Printer className="mr-2 h-4 w-4" />
            Imprimir
          </Button>
          <Button onClick={handleDownload} className="bg-blue-600 hover:bg-blue-700" data-testid="pdf-download-btn">
            <Download className="mr-2 h-4 w-4" />
            Salvar PDF
          </Button>
        </div>
      </div>

      {/* Dialog de Filtros de Impressao */}
      {showFilterDialog && (
        <div className="print:hidden fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="pdf-filter-dialog">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="p-4 border-b">
              <h2 className="text-lg font-bold">Opções de Impressão</h2>
              <p className="text-sm text-gray-500">Configure o que será exibido no PDF gerado</p>
            </div>

            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              {/* Exibir Fornecedores */}
              <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-gray-50" data-testid="filter-fornecedores">
                <input type="checkbox" checked={filterOptions.exibirFornecedores}
                  onChange={(e) => setFilterOptions(p => ({ ...p, exibirFornecedores: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600" />
                <div>
                  <span className="font-medium text-sm">Exibir fornecedores</span>
                  <p className="text-xs text-gray-500">Mostra coluna de fornecedor na tabela e seção de fornecedores envolvidos</p>
                </div>
              </label>

              {/* Exibir Valores - bloqueado para producao/pode_ver_custos=false */}
              <label className={`flex items-center gap-3 p-2 rounded-lg ${podeVerCustos ? 'cursor-pointer hover:bg-gray-50' : 'cursor-not-allowed opacity-60 bg-gray-50'}`} data-testid="filter-valores">
                <input type="checkbox" checked={filterOptions.exibirValores}
                  disabled={!podeVerCustos}
                  onChange={(e) => setFilterOptions(p => ({ ...p, exibirValores: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 disabled:opacity-50" />
                <div>
                  <span className="font-medium text-sm">Exibir valores dos itens e valor total do projeto</span>
                  <p className="text-xs text-gray-500">
                    {podeVerCustos
                      ? 'Mostra valores unitários, totais por item e valor total geral'
                      : 'Bloqueado para seu perfil (Produção / sem permissão de custos)'}
                  </p>
                </div>
              </label>

              {/* Exibir Produtos Vinculados */}
              <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-gray-50" data-testid="filter-produtos-vinculados">
                <input type="checkbox" checked={filterOptions.exibirProdutosVinculados}
                  onChange={(e) => setFilterOptions(p => ({ ...p, exibirProdutosVinculados: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600" />
                <div>
                  <span className="font-medium text-sm">Exibir produtos / serviços vinculados aos itens</span>
                  <p className="text-xs text-gray-500">Quando desligado, gera uma "ficha comercial" enxuta sem mostrar peças/marcas internas</p>
                </div>
              </label>

              {/* Incluir Termo de Aceite */}
              <label className="flex items-center gap-3 cursor-pointer p-2 rounded-lg hover:bg-gray-50" data-testid="filter-termo-aceite">
                <input type="checkbox" checked={filterOptions.incluirTermoAceite}
                  onChange={(e) => setFilterOptions(p => ({ ...p, incluirTermoAceite: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600" />
                <div>
                  <span className="font-medium text-sm">Incluir termo de aceite e conformidade</span>
                  <p className="text-xs text-gray-500">Adiciona ao final do documento uma seção com declaração, local, data e campos de assinatura para cliente, responsável técnico e testemunhas</p>
                </div>
              </label>

              {/* Nao Exibir - Secao de exclusao de itens */}
              <div className="border-t pt-3" data-testid="filter-nao-exibir">
                <div className="flex justify-between items-center mb-2">
                  <div>
                    <span className="font-medium text-sm">Não exibir</span>
                    <p className="text-xs text-gray-500">Marque os itens que deseja <strong>remover</strong> da impressão ({totalExcluidos} removido{totalExcluidos !== 1 ? 's' : ''})</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={incluirTodos} className="text-xs text-blue-600 hover:underline" data-testid="filter-incluir-todos">Incluir todos</button>
                    <button onClick={excluirTodos} className="text-xs text-red-600 hover:underline" data-testid="filter-excluir-todos">Remover todos</button>
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto border rounded-lg">
                  {checklistByTipo.map(grupo => (
                    <div key={grupo.tipo}>
                      {/* Header do tipo dentro do dialog */}
                      <div className="sticky top-0 z-10 px-3 py-1.5 text-xs font-bold text-white flex items-center gap-1.5"
                        style={{ backgroundColor: TIPO_COLORS[grupo.tipo].bg }}>
                        {grupo.tipo === 'servico' && <Wrench className="h-3 w-3" />}
                        {grupo.tipo === 'especificacao' && <Ruler className="h-3 w-3" />}
                        {grupo.tipo === 'produto' && <Package className="h-3 w-3" />}
                        {grupo.label} ({grupo.itens.length})
                      </div>
                      {grupo.itens.map((item, idx) => {
                        const key = item.id || `${item.descricao}_${item.numero}`;
                        const isExcluido = !!filterOptions.itensExcluidos[key];
                        return (
                          <label key={key || idx} className={`flex items-center gap-2 px-3 py-1.5 border-b last:border-b-0 cursor-pointer text-sm ${isExcluido ? 'bg-red-50' : 'hover:bg-gray-50'}`}>
                            <input type="checkbox"
                              checked={isExcluido}
                              onChange={() => toggleItemExcluido(key)}
                              className="h-3.5 w-3.5 rounded border-gray-300 text-red-600" />
                            <span className={isExcluido ? 'text-gray-400 line-through' : 'text-gray-800'}>
                              {item.descricao}
                            </span>
                            <span className="text-xs text-gray-400 ml-auto shrink-0">{item.categoria || 'Geral'}</span>
                          </label>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-4 border-t flex justify-between items-center">
              <span className="text-sm text-gray-500">{totalItensVisiveis} de {checklist.length} itens serão impressos</span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowFilterDialog(false)} data-testid="filter-close-btn">Fechar</Button>
                <Button onClick={() => { setShowFilterDialog(false); handlePrint(); }} className="bg-blue-600 hover:bg-blue-700" data-testid="filter-print-btn">
                  <Printer className="mr-2 h-4 w-4" />Gerar PDF
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Conteudo do PDF */}
      <div ref={printRef} className="pdf-content max-w-4xl mx-auto p-8 bg-white print:p-0 print:max-w-none print:mx-0">

        {/* CAPA */}
        <div className="capa text-center mb-8 print:mb-6">
          <div className="border-4 border-blue-600 p-6">
            <Ship className="mx-auto h-12 w-12 text-blue-600 mb-3" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">CADERNO DE ESPECIFICAÇÕES</h1>
            <div className="h-1 w-24 bg-blue-600 mx-auto mb-4"></div>
            <div className="text-lg font-semibold text-gray-700 mb-1">{project.name}</div>
            <div className="text-base text-gray-600 mb-3">Embarcação #{(parseInt(embarcacaoIndex) + 1).toString().padStart(2, '0')}</div>
            {vesselModel && (
              <div className="bg-blue-50 inline-block px-3 py-1 rounded-lg mb-4">
                <span className="font-medium text-blue-800 text-sm">{vesselModel.codigo} - {vesselModel.nome}</span>
              </div>
            )}
            <div className="mt-4 text-xs text-gray-500 space-y-0.5">
              <p><strong>Cliente:</strong> {project.client}</p>
              {project.numero_empenho && <p><strong>N Empenho:</strong> {project.numero_empenho}</p>}
              {project.responsavel_tecnico && <p><strong>Responsável Técnico:</strong> {project.responsavel_tecnico}</p>}
              <p><strong>Data de Emissão:</strong> {new Date().toLocaleDateString('pt-BR')}</p>
            </div>
          </div>
        </div>

        {/* 1. DADOS DO PROJETO */}
        <section className="dados-projeto mb-4">
          <h2 className="text-base font-bold text-gray-900 border-b-2 border-blue-600 pb-1 mb-2">1. DADOS DO PROJETO</h2>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <div><span className="font-medium text-gray-600">Projeto:</span> {project.name}</div>
            <div><span className="font-medium text-gray-600">Cliente:</span> {project.client}</div>
            {project.numero_empenho && <div><span className="font-medium text-gray-600">N Empenho:</span> {project.numero_empenho}</div>}
            {project.responsavel_tecnico && <div><span className="font-medium text-gray-600">Responsável:</span> {project.responsavel_tecnico}</div>}
            {project.start_date && <div><span className="font-medium text-gray-600">Início:</span> {new Date(project.start_date).toLocaleDateString('pt-BR')}</div>}
            {project.delivery_date && <div><span className="font-medium text-gray-600">Entrega:</span> {new Date(project.delivery_date).toLocaleDateString('pt-BR')}</div>}
          </div>
        </section>

        {/* 2. MODELO DA EMBARCAÇÃO */}
        {vesselModel && (
          <section className="modelo-embarcacao mb-4">
            <h2 className="text-base font-bold text-gray-900 border-b-2 border-blue-600 pb-1 mb-2">2. MODELO DA EMBARCAÇÃO</h2>
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex gap-4 items-start">
                {embarcacao.foto_modelo && (
                  <div className="flex-shrink-0">
                    <img src={processImageUrl(embarcacao.foto_modelo)} alt={vesselModel.nome}
                      className="w-48 h-32 object-contain rounded border bg-white"
                      onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                )}
                <div className="flex-grow text-xs">
                  <div className="grid grid-cols-2 gap-2">
                    <div><span className="font-medium text-gray-600">Código:</span> {vesselModel.codigo}</div>
                    <div><span className="font-medium text-gray-600">Nome:</span> {vesselModel.nome}</div>
                  </div>
                  {vesselModel.descricao && <p className="mt-2 text-gray-600">{vesselModel.descricao}</p>}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* 3. CHECKLIST DE ESPECIFICAÇÕES - Ordem: Serviços → Especificações → Produtos */}
        {organizedChecklist.length > 0 && (
          <section className="checklist-especificacoes mb-4">
            <h2 className="text-base font-bold text-gray-900 border-b-2 border-blue-600 pb-1 mb-2">
              3. CHECKLIST DE ESPECIFICAÇÕES
            </h2>

            {organizedChecklist.map((tipoGroup) => (
              <div key={tipoGroup.tipo} className="tipo-grupo mb-4">
                {/* Header do Tipo - destaque forte com cor e icone */}
                <div className="tipo-titulo flex items-center gap-2 px-3 py-2 mb-1 text-white text-sm font-bold"
                  style={{ backgroundColor: TIPO_COLORS[tipoGroup.tipo].bg }}>
                  {tipoGroup.tipo === 'servico' && <Wrench className="h-4 w-4" />}
                  {tipoGroup.tipo === 'especificacao' && <Ruler className="h-4 w-4" />}
                  {tipoGroup.tipo === 'produto' && <Package className="h-4 w-4" />}
                  {tipoGroup.label} ({tipoGroup.total} {tipoGroup.total === 1 ? 'item' : 'itens'})
                </div>

                {tipoGroup.grupos.map((catGroup) => (
                  <div key={catGroup.categoria} className="categoria-grupo mb-2">
                    {/* Header da Categoria - destaque medio */}
                    <div className="categoria-titulo text-xs font-bold text-gray-800 bg-gray-200 px-3 py-1.5 border-l-4 mb-0.5"
                      style={{ borderLeftColor: TIPO_COLORS[tipoGroup.tipo].bg }}>
                      {catGroup.categoria} ({catGroup.itens.length})
                    </div>

                    <table className="tabela-itens w-full text-xs border-collapse mb-1">
                      <thead>
                        <tr className="bg-gray-50 border-b text-left">
                          <th className="py-1 px-1.5" style={{ width: '20px' }}></th>
                          <th className="py-1 px-1.5" style={{ width: filterOptions.exibirValores ? '20%' : '25%' }}>Item</th>
                          {filterOptions.exibirProdutosVinculados && (
                            <th className="py-1 px-1.5" style={{ width: '22%' }}>Produtos/Serviços Vinculados</th>
                          )}
                          {filterOptions.exibirFornecedores && (
                            <th className="py-1 px-1.5" style={{ width: '12%' }}>Fornecedor</th>
                          )}
                          <th className="py-1 px-1.5" style={{ width: '5%' }}>Qtd</th>
                          <th className="py-1 px-1.5" style={{ width: '18%' }}>Observações</th>
                          {filterOptions.exibirValores && (
                            <th className="py-1 px-1.5 text-right" style={{ width: '10%' }}>Valor</th>
                          )}
                          <th className="py-1 px-1.5 text-center" style={{ width: '8%' }}>Foto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {catGroup.itens.map((item, idx) => {
                          const fornecedor = item.fornecedor_id && fornecedoresMap[item.fornecedor_id];
                          const itensCusto = item.itens_custo || [];
                          const valorItem = itensCusto.reduce((s, c) => s + (parseFloat(c.valor_total) || 0), 0);

                          return (
                            <tr key={item.id || item.descricao || idx} className="linha-item border-b hover:bg-gray-50/50">
                              <td className="py-1 px-1.5 align-top">
                                {item.status
                                  ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                                  : <XCircle className="h-3.5 w-3.5 text-gray-300" />}
                              </td>
                              <td className="py-1 px-1.5 font-medium align-top">{item.descricao}</td>
                              {filterOptions.exibirProdutosVinculados && (
                                <td className="py-1 px-1.5 align-top">
                                  {itensCusto.length > 0 ? (
                                    <div className="space-y-0.5">
                                      {itensCusto.map((ic, icIdx) => (
                                        <div key={icIdx} className="flex items-start gap-1">
                                          <span className={`inline-block w-1.5 h-1.5 rounded-full mt-1 shrink-0 ${ic.tipo === 'produto' ? 'bg-amber-500' : 'bg-green-500'}`}></span>
                                          <span className="text-gray-700">
                                            {ic.produto_nome || ic.descricao_manual || ic.descricao || '-'}
                                            {ic.quantidade && ic.quantidade > 1 ? ` (x${ic.quantidade})` : ''}
                                            {filterOptions.exibirValores && ic.valor_total ? (
                                              <span className="text-gray-400 ml-1">
                                                R$ {parseFloat(ic.valor_total).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                              </span>
                                            ) : null}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-gray-300">-</span>
                                  )}
                                </td>
                              )}
                              {filterOptions.exibirFornecedores && (
                                <td className="py-1 px-1.5 align-top text-gray-600">
                                  {fornecedor?.razao_social || fornecedor?.nome || '-'}
                                </td>
                              )}
                              <td className="py-1 px-1.5 align-top">{item.quantidade || '-'}</td>
                              <td className="py-1 px-1.5 text-gray-600 align-top">{item.observacao || '-'}</td>
                              {filterOptions.exibirValores && (
                                <td className="py-1 px-1.5 text-right align-top font-medium">
                                  {valorItem > 0 ? `R$ ${valorItem.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '-'}
                                </td>
                              )}
                              <td className="py-1 px-1.5 align-top text-center">
                                {item.foto_url ? (
                                  <img src={processImageUrl(item.foto_url)} alt={item.descricao}
                                    className="foto-item inline-block w-14 h-10 object-contain rounded border"
                                    onError={(e) => { e.target.style.display = 'none'; }} />
                                ) : (
                                  <span className="text-gray-300">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            ))}

            {/* Valor Total do Projeto */}
            {filterOptions.exibirValores && valorTotal > 0 && (
              <div className="valor-total bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-bold text-blue-800">VALOR TOTAL DO PROJETO</span>
                  <span className="text-lg font-bold text-blue-900">
                    R$ {valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            )}
          </section>
        )}

        {/* 4. GALERIA DE FOTOS */}
        {itensComFoto.length > 0 && (
          <section className="galeria-fotos mb-4">
            <h2 className="text-base font-bold text-gray-900 border-b-2 border-blue-600 pb-1 mb-2">
              4. GALERIA DE ESPECIFICAÇÕES TÉCNICAS
            </h2>
            <p className="text-xs text-gray-500 mb-2">Fotos detalhadas dos itens especificados</p>
            <div className="grid grid-cols-3 gap-2">
              {itensComFoto.map((item, idx) => (
                <div key={item.id || item.descricao || idx} className="foto-detalhe border rounded p-1">
                  <img src={processImageUrl(item.foto_url)} alt={item.descricao}
                    className="w-full h-28 object-contain bg-gray-50 rounded mb-1"
                    onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
                  <p className="text-xs font-medium text-gray-800 truncate">{item.descricao}</p>
                  <p className="text-xs text-gray-500 truncate">{item.categoria}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* 5. FORNECEDORES ENVOLVIDOS */}
        {filterOptions.exibirFornecedores && fornecedoresUsados.length > 0 && (
          <section className="fornecedores mb-4">
            <h2 className="text-base font-bold text-gray-900 border-b-2 border-blue-600 pb-1 mb-2">
              {itensComFoto.length > 0 ? '5' : '4'}. FORNECEDORES ENVOLVIDOS
            </h2>
            <div className="grid grid-cols-3 gap-2">
              {fornecedoresUsados.map((fId, idx) => {
                const f = fornecedoresMap[fId];
                if (!f) return null;
                return (
                  <div key={f.id || f.razao_social || idx} className="fornecedor-card border p-2 rounded text-xs">
                    <div className="font-semibold text-gray-900 truncate">{f.razao_social || f.nome}</div>
                    {f.nome_fantasia && <div className="text-gray-600 truncate">{f.nome_fantasia}</div>}
                    {f.telefone && <div className="text-gray-500">Tel: {f.telefone}</div>}
                    {f.email && <div className="text-gray-500 truncate">{f.email}</div>}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* 6. TERMO DE ACEITE E CONFORMIDADE */}
        {filterOptions.incluirTermoAceite && (
          <section className="termo-aceite mb-4 mt-6" data-testid="pdf-termo-aceite">
            <h2 className="text-base font-bold text-gray-900 border-b-2 border-blue-600 pb-1 mb-3 uppercase tracking-wide">
              Termo de Aceite e Conformidade
            </h2>
            <div className="border border-gray-300 rounded p-4 bg-gray-50">
              <p className="text-sm text-gray-800 leading-relaxed mb-4 text-justify">
                Declaro que li, compreendi e estou de acordo com todas as informações constantes
                nesta ficha técnica da embarcação, conferindo a especificação técnica, os componentes
                e demais dados aqui descritos como condizentes com o pedido.
              </p>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-xs text-gray-600 mb-0.5">Local:</p>
                  <div className="border-b border-gray-400 h-5"></div>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-0.5">Data:</p>
                  <div className="border-b border-gray-400 h-5"></div>
                </div>
              </div>

              <div className="mt-8">
                <div className="border-b border-gray-700 h-10 mb-1 max-w-md mx-auto"></div>
                <p className="text-xs font-semibold text-gray-900 text-center">CLIENTE</p>
                <p className="text-xs text-gray-700 mt-0.5 text-center">{project?.client || ''}</p>
              </div>
            </div>
          </section>
        )}

        {/* RODAPE */}
        <footer className="rodape mt-6 pt-3 border-t text-center text-xs text-gray-400">
          <p>Documento gerado por NautilusOS em {new Date().toLocaleString('pt-BR')}</p>
          <p>{project.name} — Embarcação #{(parseInt(embarcacaoIndex) + 1).toString().padStart(2, '0')}</p>
        </footer>
      </div>

      {/* Estilos de impressao */}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 8mm;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
            font-size: 8pt;
          }
          .print\\:hidden { display: none !important; }
          .pdf-content {
            padding: 0 !important;
            max-width: 100% !important;
          }

          /* Capa - pagina propria */
          .capa {
            page-break-after: always;
            margin-bottom: 0 !important;
          }

          /* Secoes */
          section { page-break-inside: auto; }
          section h2 { page-break-after: avoid; }

          /* Tipo header - nao separar do conteudo */
          .tipo-titulo { page-break-after: avoid; }
          .tipo-grupo { page-break-inside: auto; }

          /* Categoria - manter header com itens */
          .categoria-grupo { page-break-inside: auto; }
          .categoria-titulo { page-break-after: avoid; }

          /* Tabelas */
          .tabela-itens { page-break-inside: auto; }
          .tabela-itens thead { display: table-header-group; }
          .linha-item { page-break-inside: avoid; }

          /* Fotos */
          .foto-item { width: 45px !important; height: 32px !important; }
          .galeria-fotos { page-break-inside: auto; }
          .foto-detalhe { page-break-inside: avoid; break-inside: avoid; }

          /* Fornecedores */
          .fornecedor-card { page-break-inside: avoid; }

          /* Valor total - nao separar */
          .valor-total { page-break-inside: avoid; }

          /* Rodape */
          .rodape { page-break-inside: avoid; margin-top: 8px !important; }

          img { page-break-inside: avoid; }

          /* Cores de fundo nos headers - forcar impressao */
          .tipo-titulo {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .categoria-titulo {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          /* Espacamentos compactos para impressao */
          .mb-4 { margin-bottom: 6px !important; }
          .mb-3 { margin-bottom: 5px !important; }
          .mb-2 { margin-bottom: 3px !important; }
          .mb-1 { margin-bottom: 2px !important; }
          .py-1 { padding-top: 2px !important; padding-bottom: 2px !important; }
          .px-1\\.5 { padding-left: 3px !important; padding-right: 3px !important; }
          .p-1 { padding: 2px !important; }
          .p-2 { padding: 4px !important; }
          .p-3 { padding: 6px !important; }
          .gap-2 { gap: 4px !important; }
          .text-xs { font-size: 7pt !important; }
          .text-sm { font-size: 8pt !important; }
          .text-base { font-size: 9pt !important; }
        }
      `}</style>
    </>
  );
};

export default EspecificacaoPDF;
